import { useEffect, useState, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Container,
  Header,
  Modal,
  Pagination,
  SpaceBetween,
  StatusIndicator,
  Table,
  TextFilter,
} from '@cloudscape-design/components';
import routes from 'src/contstant/routePaths';
import {
  getItemValue,
  getQuestionSelectionTableColumns,
  getSearchableFields,
} from './QuestionSelectionsConfig';
import { Question, TagMap } from 'src/types/helios/Question';
import { useQuestion } from 'src/store/question/useQuestion';
import useUserProfile from 'src/hooks/useUserProfile';
import QuestionDetailsPage from 'src/pages/question-details/QuestionDetailsPage';
import { useLocalPagination } from 'src/hooks/useLocalPagination';
import { useGenericFilter } from 'src/hooks/useGenericFilter';
import { convertList } from 'src/utils/generalUtils';
import { openQuestionDetailsPage } from 'src/helper/questionHelper';
import { DEFAULT_LANGUAGE } from 'src/contstant/commonLabels';
import { AuditTemplate } from '@amzn/gbsa-helios-typescript-client';

interface QuestionSelectionProps {
  tags?: TagMap;
  division?: string;
  data?: AuditTemplate | null;
  selectedQuestions: string[];
  onViewDismiss: () => void;
  onSave: (unselectedQuestionIds: string[]) => void;
}

const QuestionSelection = ({
  tags,
  data,
  division,
  onViewDismiss,
  selectedQuestions,
  onSave,
}: QuestionSelectionProps) => {
  const { searchQuestionApiStatus, searchQuestions, questionsList } = useQuestion();
  const { getUserBusinessUnitId, getLanguage } = useUserProfile();
  const navigate = useNavigate();
  const { searchText, setSearchText, filteredItems } = useGenericFilter<Question>(
    questionsList,
    getItemValue,
    getSearchableFields,
  );
  // Local pagination
  const { currentPageIndex, setCurrentPageIndex, paginatedItems, totalPages } =
    useLocalPagination({
      items: filteredItems || [],
      pageSize: 10,
    });

  const [excludedItems, setExcludedItems] = useState<Question[]>([]);

  useEffect(() => {
    searchQuestions({
      businessUnitId: getUserBusinessUnitId(),
      language: DEFAULT_LANGUAGE,
      isActive: true,
      tagFilters: tags,
      divisionName: division,
    });
  }, [tags, getUserBusinessUnitId()]);

  useEffect(() => {
    const excludedQuestionsSet = new Set(selectedQuestions);
    setExcludedItems(questionsList.filter((q) => excludedQuestionsSet.has(q.questionId)));
  }, [questionsList, selectedQuestions]);

  const handleSave = () => {
    onSave(convertList<Question, string>(excludedItems, (item) => item.questionId));
  };

  const onViewDetails = (question: Question) => {
    openQuestionDetailsPage(navigate, question, false, true);
  };
  return (
    <div>
      <Table
        footer={
          <SpaceBetween direction="horizontal" size="xs">
            <Button variant="link" onClick={onViewDismiss}>
              Cancel
            </Button>
            <Button variant="primary" onClick={handleSave}>
              Apply Exclusions ({excludedItems.length}/{questionsList.length})
            </Button>
          </SpaceBetween>
        }
        columnDefinitions={getQuestionSelectionTableColumns(
          onViewDetails,
          convertList<Question, string>(excludedItems, (item) => item.questionId),
        )}
        items={paginatedItems as Question[]}
        selectionType="multi"
        selectedItems={excludedItems}
        onSelectionChange={({ detail }) => setExcludedItems(detail.selectedItems)}
        filter={
          <TextFilter
            filteringText={searchText}
            filteringPlaceholder="Search "
            filteringAriaLabel="Search "
            onChange={({ detail }) => {
              setSearchText(detail.filteringText);
            }}
          />
        }
        loading={searchQuestionApiStatus.isLoading}
        loadingText="Loading ..."
        pagination={
          <Pagination
            currentPageIndex={currentPageIndex}
            pagesCount={totalPages}
            onChange={({ detail }) => setCurrentPageIndex(detail.currentPageIndex)}
          />
        }
        empty={
          <Box margin={{ vertical: 'xs' }} textAlign="center" color="inherit">
            <SpaceBetween size="m">
              <StatusIndicator type="warning">No Questions.</StatusIndicator>
            </SpaceBetween>
          </Box>
        }
        header={
          <Header
            variant="h3"
            description="Select questions to exclude from this template"
          >
            Questions for Template
          </Header>
        }
      />
    </div>
  );
};

export default QuestionSelection;
